import React, { useEffect, useState, useRef } from 'react';
import { XIcon, DownloadIcon, FileTextIcon, LoaderIcon, EyeIcon, PaletteIcon } from 'lucide-react';
import { generateProgressReport, generateProgressReportBlob } from '../../services/progressReportService';
import { Lesson } from '../../src/types/lesson';
import { Task } from '../../services/taskService';
import toast from 'react-hot-toast';
import { PdfPreviewViewer } from '../pdf/PdfPreviewViewer';
interface ExportProgressReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: {
    id: string;
    name: string;
    instrument: string;
    attendanceValue: number;
    taskCompletionRate: string;
    progressTrend: string;
  } | null;
  teacherName: string;
  lessons: Lesson[]; // Pre-fetched lessons for this student
  tasks: Task[]; // Pre-fetched tasks for this student
  darkMode: boolean;
}
export const ExportProgressReportModal: React.FC<ExportProgressReportModalProps> = ({
  isOpen,
  onClose,
  student,
  teacherName,
  lessons,
  tasks,
  darkMode
}) => {
  // Default date range: Last 3 months to today
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [sections, setSections] = useState({
    lessonHistory: true,
    lessonNotes: true,
    completedTasks: true,
    progressSummary: true
  });
  // New State for Phase 3
  const [headerColor, setHeaderColor] = useState('#1A2A52'); // Default Navy
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [messageToParent, setMessageToParent] = useState('');
  // Track whether the desktop iframe has finished loading its content
  const [iframeReady, setIframeReady] = useState(false);
  // Mobile preview scaling
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const [mobileScale, setMobileScale] = useState(1);
  const PDF_NATIVE_WIDTH = 612; // US Letter width in points
  const PDF_NATIVE_HEIGHT = 792; // US Letter height in points
  // Store raw PDF blob for canvas-based mobile preview
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  // Measure container width and calculate scale for mobile
  useEffect(() => {
    const container = previewContainerRef.current;
    if (!container || !isOpen) return;
    const updateScale = () => {
      const containerWidth = container.clientWidth;
      // Only scale down if container is narrower than PDF
      if (containerWidth < PDF_NATIVE_WIDTH) {
        setMobileScale(containerWidth / PDF_NATIVE_WIDTH);
      } else {
        setMobileScale(1);
      }
    };
    updateScale();
    const resizeObserver = new ResizeObserver(updateScale);
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [isOpen]);
  // Clean up object URL on unmount or when previewUrl changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);
  // Reset stale preview state when modal opens to prevent rendering revoked blob URLs
  // Set isPreviewLoading true immediately so loader shows instead of empty state flash
  useEffect(() => {
    if (isOpen) {
      setPreviewUrl(null);
      setPdfBlob(null);
      setIframeReady(false);
      setIsPreviewLoading(true);
    }
  }, [isOpen]);
  // Reset iframeReady whenever previewUrl changes (new PDF being loaded)
  useEffect(() => {
    setIframeReady(false);
  }, [previewUrl]);
  // Auto-generate preview when modal opens or inputs change (debounced)
  useEffect(() => {
    if (isOpen && student) {
      const timer = setTimeout(() => {
        handlePreview();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen, student, startDate, endDate, sections, headerColor]);
  if (!isOpen || !student) return null;
  // Helper to convert hex to RGB array
  const hexToRgb = (hex: string): [number, number, number] => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : [26, 42, 82];
  };
  const getFilteredData = () => {
    const start = new Date(startDate + 'T00:00:00');
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate + 'T00:00:00');
    end.setHours(23, 59, 59, 999);
    const filteredLessons = lessons.filter(l => {
      const d = new Date(l.startAt);
      return d >= start && d <= end;
    });
    const filteredTasks = tasks.filter(t => {
      const d = new Date(t.completedDate || t.dueDate || t.createdAt || 0);
      return d >= start && d <= end;
    });
    return {
      start,
      end,
      filteredLessons,
      filteredTasks
    };
  };
  const handlePreview = async () => {
    if (!startDate || !endDate) return;
    const {
      start,
      end,
      filteredLessons,
      filteredTasks
    } = getFilteredData();
    if (start > end) return;
    setIsPreviewLoading(true);
    setIframeReady(false);
    try {
      const blob = await generateProgressReportBlob({
        studentName: student.name,
        instrument: student.instrument || 'Music',
        teacherName,
        dateRange: {
          from: start,
          to: end
        },
        sections,
        lessons: filteredLessons,
        tasks: filteredTasks,
        metrics: {
          attendanceRate: student.attendanceValue,
          taskCompletionRate: student.taskCompletionRate,
          progressTrend: student.progressTrend
        },
        branding: {
          headerColor: hexToRgb(headerColor)
        },
        messageToParent: messageToParent.trim() || undefined
      });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPdfBlob(blob);
    } catch (error) {
      console.error('Error generating preview:', error);
    } finally {
      setIsPreviewLoading(false);
    }
  };
  // Called when the desktop iframe finishes loading the PDF content
  const handleIframeLoad = () => {
    setIframeReady(true);
  };
  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select a valid date range');
      return;
    }
    const {
      start,
      end,
      filteredLessons,
      filteredTasks
    } = getFilteredData();
    if (start > end) {
      toast.error('Start date must be before end date');
      return;
    }
    setIsGenerating(true);
    try {
      await generateProgressReport({
        studentName: student.name,
        instrument: student.instrument || 'Music',
        teacherName,
        dateRange: {
          from: start,
          to: end
        },
        sections,
        lessons: filteredLessons,
        tasks: filteredTasks,
        metrics: {
          attendanceRate: student.attendanceValue,
          taskCompletionRate: student.taskCompletionRate,
          progressTrend: student.progressTrend
        },
        branding: {
          headerColor: hexToRgb(headerColor)
        },
        messageToParent: messageToParent.trim() || undefined
      });
      toast.success('Report downloaded successfully');
      onClose();
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate report');
    } finally {
      setIsGenerating(false);
    }
  };
  return <div className="fixed inset-0 z-[60] overflow-y-auto" data-id="element-2098">
      {/* Mobile: full-screen sheet. Desktop: centered dialog */}
      <div className="flex items-end md:items-center justify-center min-h-screen md:px-4 md:pt-4 md:pb-20 text-center sm:block sm:p-0" data-id="element-2099">
        <div className="fixed inset-0 transition-opacity" onClick={onClose} data-id="element-2100">
          <div className={`absolute inset-0 ${darkMode ? 'bg-navy-900' : 'bg-gray-900'} opacity-75`} data-id="element-2101"></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-2102">
          &#8203;
        </span>
        <div className={`fixed inset-0 w-full max-w-full md:relative md:inset-auto md:inline-block md:align-middle rounded-none md:rounded-xl text-left overflow-hidden shadow-xl transform transition-all md:my-8 md:max-w-5xl md:w-full flex flex-col box-border ${darkMode ? 'bg-navy-800 md:border md:border-navy-600' : 'bg-white md:border md:border-gray-200'}`} data-id="element-2103">
          {/* Header */}
          <div className={`px-6 py-4 border-b flex items-center justify-between shrink-0 ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-2104">
            <div className="flex items-center gap-2" data-id="element-2105">
              <FileTextIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2106" />
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2107">
                Export Progress Report
              </h3>
            </div>
            <button onClick={onClose} className={`rounded-full p-1.5 transition-colors ${darkMode ? 'hover:bg-navy-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'}`} data-id="element-2108">
              <XIcon className="h-5 w-5" data-id="element-2109" />
            </button>
          </div>

          <div className="flex flex-col md:flex-row flex-1 min-h-0 md:h-[600px] overflow-y-auto md:overflow-hidden w-full max-w-full" data-id="element-2110">
            {/* Left Column: Controls */}
            <div className={`w-full max-w-full md:w-1/3 min-w-0 px-4 py-6 md:p-6 md:overflow-y-auto shrink-0 box-border overflow-x-hidden md:border-r ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-2111">
              <div className="space-y-6 min-w-0" data-id="element-2112">
                {/* Student Info */}
                <div data-id="element-2113">
                  <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2114">
                    Student
                  </p>
                  <p className={`text-base ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2115">
                    {student.name}{' '}
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2116">
                      ({student.instrument || 'Music'})
                    </span>
                  </p>
                </div>
                {/* Date Range */}
                <div data-id="element-2117">
                  <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2118">
                    Date Range
                  </p>
                  <div className="flex flex-col gap-2" data-id="element-2119">
                    {/* Desktop: native date inputs */}
                    <div className="hidden md:contents" data-id="element-2120">
                      <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent outline-none transition-colors ${darkMode ? 'bg-navy-900/50 border-navy-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`} data-id="element-2121" />
                      <div className={`text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2122">
                        to
                      </div>
                      <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent outline-none transition-colors ${darkMode ? 'bg-navy-900/50 border-navy-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`} data-id="element-2123" />
                    </div>
                    {/* Mobile: styled containers with hidden native date inputs */}
                    <div className="md:hidden flex flex-col gap-2" data-id="element-2124">
                      <label className="relative block w-full" data-id="element-2125">
                        <span className={`block w-full px-3 py-2 rounded-lg border text-center transition-colors ${darkMode ? 'bg-navy-900/50 border-navy-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`} data-id="element-2126">
                          {startDate ? new Date(startDate + 'T00:00:00').toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        }) : 'Start date'}
                        </span>
                        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-2127" />
                      </label>
                      <div className={`text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2128">
                        to
                      </div>
                      <label className="relative block w-full" data-id="element-2129">
                        <span className={`block w-full px-3 py-2 rounded-lg border text-center transition-colors ${darkMode ? 'bg-navy-900/50 border-navy-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`} data-id="element-2130">
                          {endDate ? new Date(endDate + 'T00:00:00').toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        }) : 'End date'}
                        </span>
                        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-2131" />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Sections */}
                <div data-id="element-2132">
                  <p className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2133">
                    Include in Report
                  </p>
                  <div className="space-y-3" data-id="element-2134">
                    <label className="flex items-center gap-3 cursor-pointer" data-id="element-2135">
                      <input type="checkbox" checked={sections.progressSummary} onChange={e => setSections(s => ({
                      ...s,
                      progressSummary: e.target.checked
                    }))} className="w-4 h-4 text-[#3B82F6] rounded border-gray-300 focus:ring-[#3B82F6]" data-id="element-2136" />
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2137">
                        Progress Summary
                      </span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer" data-id="element-2138">
                      <input type="checkbox" checked={sections.lessonHistory} onChange={e => setSections(s => ({
                      ...s,
                      lessonHistory: e.target.checked
                    }))} className="w-4 h-4 text-[#3B82F6] rounded border-gray-300 focus:ring-[#3B82F6]" data-id="element-2139" />
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2140">
                        Lesson History
                      </span>
                    </label>
                    <label className={`flex items-center gap-3 cursor-pointer ml-7 ${!sections.lessonHistory ? 'opacity-50' : ''}`} data-id="element-2141">
                      <input type="checkbox" checked={sections.lessonNotes} disabled={!sections.lessonHistory} onChange={e => setSections(s => ({
                      ...s,
                      lessonNotes: e.target.checked
                    }))} className="w-4 h-4 text-[#3B82F6] rounded border-gray-300 focus:ring-[#3B82F6] disabled:opacity-50" data-id="element-2142" />
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2143">
                        Include Teacher Notes
                      </span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer" data-id="element-2144">
                      <input type="checkbox" checked={sections.completedTasks} onChange={e => setSections(s => ({
                      ...s,
                      completedTasks: e.target.checked
                    }))} className="w-4 h-4 text-[#3B82F6] rounded border-gray-300 focus:ring-[#3B82F6]" data-id="element-2145" />
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2146">
                        Completed Tasks
                      </span>
                    </label>
                  </div>
                </div>

                {/* Message to Parent */}
                <div data-id="element-2147">
                  <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2148">
                    Teacher Message
                  </p>
                  <textarea value={messageToParent} onChange={e => setMessageToParent(e.target.value)} rows={4} placeholder="e.g., Great progress this month! Focus on scales next..." className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent outline-none transition-colors resize-none ${darkMode ? 'bg-navy-900/50 border-navy-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`} data-id="element-2149" />
                  <button type="button" onClick={handlePreview} disabled={isPreviewLoading} className={`mt-2 w-full px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${darkMode ? 'bg-navy-700 hover:bg-navy-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} disabled:opacity-50 disabled:cursor-not-allowed`} data-id="element-2150">
                    <EyeIcon className="h-3.5 w-3.5" data-id="element-2151" />
                    Update Preview
                  </button>
                </div>

                {/* Branding */}
                <div data-id="element-2152">
                  <p className={`text-sm font-medium mb-2 flex items-center gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2153">
                    <PaletteIcon className="h-4 w-4" data-id="element-2154" />
                    Branding
                  </p>
                  <div className="flex items-center gap-3" data-id="element-2155">
                    <input type="color" value={headerColor} onChange={e => setHeaderColor(e.target.value)} className="h-8 w-8 rounded cursor-pointer border-0 p-0" data-id="element-2156" />
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2157">
                      Header Color
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Preview — stacked below on mobile, side-by-side on desktop */}
            <div ref={previewContainerRef} className={`w-full md:w-2/3 bg-white dark:bg-gray-900 relative min-h-[60vh] md:min-h-0 md:h-full overflow-hidden`} data-id="element-2158">
              {/* Empty state placeholder — visible when no preview is ready */}
              <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-150 ${previewUrl && iframeReady ? 'opacity-0 pointer-events-none' : !previewUrl && !isPreviewLoading ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-id="element-2159">
                <div className="text-center" data-id="element-2160">
                  <EyeIcon className={`h-12 w-12 mx-auto mb-3 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-2161" />
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2162">
                    Preview will appear here
                  </p>
                </div>
              </div>

              {/* Desktop: standard full-size iframe — always mounted, only visible when loaded */}
              <iframe src={previewUrl ? `${previewUrl}#toolbar=0&navpanes=0&scrollbar=0` : 'about:blank'} onLoad={previewUrl ? handleIframeLoad : undefined} className={`hidden md:block absolute inset-0 w-full h-full border-0 transition-opacity duration-150 ${previewUrl && iframeReady ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} title="PDF Preview" data-id="element-2163" />

              {/* Mobile: canvas-based PDF viewer */}
              {previewUrl && <div className="md:hidden w-full h-full overflow-y-auto p-2" data-id="element-2164">
                  <PdfPreviewViewer pdfBlob={pdfBlob} darkMode={darkMode} data-id="element-2165" />
                </div>}

              {/* Loading overlay — visible while generating or waiting for iframe to load */}
              {isPreviewLoading && <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-black/50 backdrop-blur-sm" data-id="element-2166">
                  <div className="flex flex-col items-center" data-id="element-2167">
                    <LoaderIcon className="h-8 w-8 animate-spin text-[#3B82F6] mb-2" data-id="element-2168" />
                    <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2169">
                      Generating Preview...
                    </span>
                  </div>
                </div>}
            </div>
          </div>

          {/* Footer — sticky at bottom on mobile */}
          <div className={`px-6 py-4 border-t flex justify-end gap-3 shrink-0 ${darkMode ? 'border-navy-700 bg-navy-800/50' : 'border-gray-200 bg-gray-50'}`} data-id="element-2170">
            <button onClick={onClose} disabled={isGenerating} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode ? 'text-gray-300 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-200'}`} data-id="element-2171">
              Cancel
            </button>
            <button onClick={handleGenerate} disabled={isGenerating} className="px-4 py-2 rounded-lg text-sm font-medium bg-[#3B82F6] hover:bg-blue-600 text-white transition-colors flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-2172">
              {isGenerating ? <>
                  <LoaderIcon className="h-4 w-4 animate-spin" data-id="element-2173" />
                  Generating...
                </> : <>
                  <DownloadIcon className="h-4 w-4" data-id="element-2174" />
                  Download PDF
                </>}
            </button>
          </div>
        </div>
      </div>
    </div>;
};